//
/* GS://PROTOCOLO
 * Arquivo: src/components/modals/PayAccountModal.jsx
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.6 – CLEAN: Interface de Pagamento
 * Impacto da Versão: [src/components/modals/PayAccountModal.jsx]
 * Data: 25/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [REFACTOR]: Removeu chamadas diretas ao `financialService`. Agora utiliza exclusivamente `payAccount` do `firestoreService`.
 * - [UX]: Passa a `selectedCategory` como parâmetro `ledgerOptions` para a função de pagamento.
 */
//

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { payAccount } from '../../services/firestoreService';
import { Loader, X, Check, DollarSign } from 'lucide-react';

const PayAccountModal = ({ account, onClose, onSuccess }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Estados do Formulário
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [paidAmountRaw, setPaidAmountRaw] = useState(account.amount * 100); 
    const [paidAmountInput, setPaidAmountInput] = useState(
        (account.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    );
    const [paymentMethod, setPaymentMethod] = useState('Pix');
    
    // [GS-UX]: Estado para categoria (pré-carrega a original, mas permite troca)
    const [selectedCategory, setSelectedCategory] = useState(account.category || 'Outras Despesas');

    // Lista estática de categorias
    const availableCategories = [
        "Impostos sobre Compras",
        "Compras de Estoque",
        "Despesas Operacionais",
        "Marketing",
        "Infraestrutura",
        "Pessoal",
        "Outras Despesas"
    ];

    const handleAmountChange = (e) => {
        const value = e.target.value.replace(/\D/g, ''); 
        let cents = parseInt(value, 10);
        if (isNaN(cents)) cents = 0;
        const paddedCents = String(cents).padStart(3, '0'); 
        const integerPart = paddedCents.slice(0, -2);
        const decimalPart = paddedCents.slice(-2);
        setPaidAmountInput(`${parseInt(integerPart, 10).toLocaleString('pt-BR')},${decimalPart}`);
        setPaidAmountRaw(cents);
    };

    const handleConfirmPayment = async (e) => {
        e.preventDefault();
        
        const finalAmount = paidAmountRaw / 100;
        if (finalAmount <= 0) {
            toast.warning("O valor pago deve ser maior que zero.");
            return;
        }

        setIsProcessing(true);
        try {
            // [GS-CORE]: Chama a função refatorada que atualiza TUDO (Batch)
            await payAccount({
                accountId: account.id,
                originalAccount: account, 
                paymentData: {
                    date: paymentDate,
                    amount: finalAmount,
                    method: paymentMethod,
                    category: selectedCategory // Passa a categoria corrigida
                }
            });
            
            toast.success(`Conta baixada e extrato atualizado!`);
            onSuccess(); 
            onClose();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao processar pagamento. Tente novamente.");
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                <div className="bg-brand-dark p-4 flex justify-between items-center border-b border-gray-700">
                    <h3 className="text-white font-bold text-lg flex items-center gap-2">
                        <DollarSign className="text-brand-gold" /> Confirmar Pagamento
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition">
                        <X size={24} />
                    </button>
                </div>
                <div className="bg-gray-50 p-4 border-b border-gray-200">
                    <p className="text-xs text-gray-500 uppercase font-bold">Pagando conta de:</p>
                    <p className="text-gray-800 font-semibold text-lg truncate">{account.description}</p>
                    <div className="flex justify-between mt-2 text-sm">
                        <span className="text-gray-600">Vencimento Original:</span>
                        <span className="font-mono font-medium">
                            {account.dueDate ? new Date(account.dueDate).toLocaleDateString('pt-BR') : '-'}
                        </span>
                    </div>
                </div>
                <form onSubmit={handleConfirmPayment} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Data do Pagamento</label>
                        <input 
                            type="date"
                            required
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 bg-white"
                        />
                    </div>
                    
                    {/* [GS-UX]: Seletor de Categoria */}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Categoria Contábil</label>
                        <select 
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700"
                        >
                            {availableCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-gray-400 mt-1">Confirme a categoria para o relatório.</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Valor Efetivamente Pago (R$)</label>
                        <input 
                            type="text"
                            required
                            value={paidAmountInput}
                            onChange={handleAmountChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none font-mono text-lg font-bold text-gray-800"
                        />
                        {(paidAmountRaw / 100) !== account.amount && (
                            <p className="text-xs mt-1 font-medium text-orange-600">
                                {(paidAmountRaw / 100) > account.amount ? 'Incluindo juros/multa' : 'Com desconto aplicado'}
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-1">Meio de Pagamento</label>
                        <select 
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none bg-white text-gray-700"
                        >
                            <option value="Pix">Pix</option>
                            <option value="Dinheiro">Dinheiro (Espécie)</option>
                            <option value="Transferência Bancária">Transferência Bancária</option>
                            <option value="Cartão de Crédito">Cartão de Crédito</option>
                            <option value="Cartão de Débito">Cartão de Débito</option>
                            <option value="Boleto">Boleto</option>
                        </select>
                    </div>
                    <div className="flex gap-3 mt-6 pt-2">
                        <button 
                            type="button" 
                            onClick={onClose}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 font-semibold transition"
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            disabled={isProcessing}
                            className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-bold shadow-md transition flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? <Loader className="animate-spin" /> : <Check />}
                            Confirmar Baixa
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PayAccountModal;